import { makeStyles, Theme } from '@material-ui/core/styles'

export const useVoiceAgentStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
        backgroundColor: theme.palette.background.default,
    },
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2.5, 4),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        gap: theme.spacing(2),
        width: '100%',
        boxSizing: 'border-box',
    },
    headerLeft: {
        minWidth: 0,
        flex: 1,
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexShrink: 0,
    },
    title: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.3,
    },
    pageSubtitle: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    content: {
        flex: 1,
        padding: theme.spacing(4),
        maxWidth: 880,
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    card: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(3),
    },
    statusChip: {
        fontWeight: 600,
        height: 26,
        '& .MuiChip-label': {
            padding: theme.spacing(0, 1.25),
        },
    },
    liveChip: {
        backgroundColor: '#ecfdf5',
        color: '#15803d',
    },
    pausedChip: {
        backgroundColor: '#fefce8',
        color: '#a16207',
    },
    pendingChip: {
        backgroundColor: '#ecfdf5',
        color: '#15803d',
    },

    emptyRoot: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        padding: theme.spacing(4),
    },
    emptyCard: {
        textAlign: 'center',
        maxWidth: 440,
        padding: theme.spacing(6, 4),
    },
    emptyIconWrap: {
        width: 56,
        height: 56,
        borderRadius: 16,
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
    },

    numberHero: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(4, 3),
        textAlign: 'center',
    },
    numberLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    numberDisplay: {
        fontSize: '2.25rem',
        fontWeight: 700,
        letterSpacing: 0.5,
        lineHeight: 1.2,
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            fontSize: '1.6rem',
        },
    },
    numberHint: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        marginTop: theme.spacing(2),
        maxWidth: 420,
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    metaRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    metaCard: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2, 2.5),
    },
    metaLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginBottom: 4,
    },
    metaValue: {
        fontWeight: 600,
        fontSize: '1rem',
        textTransform: 'capitalize',
    },

    wizardRoot: {
        maxWidth: 640,
        margin: '0 auto',
        width: '100%',
    },
    setupContent: {
        flex: 1,
        padding: theme.spacing(3, 4),
        width: '100%',
        maxWidth: 'none',
        margin: 0,
        boxSizing: 'border-box',
    },
    setupWide: {
        width: '100%',
        maxWidth: 'none',
    },
    knowledgeSplit: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        columnGap: theme.spacing(10),
        rowGap: theme.spacing(4),
        alignItems: 'start',
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        },
    },
    knowledgeSplitSide: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minWidth: 0,
    },
    stepPills: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    stepPill: {
        flex: 1,
        textAlign: 'center',
        padding: theme.spacing(1, 1),
        borderRadius: 10,
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[100],
        transition: 'all 0.2s ease',
        border: `1.5px solid transparent`,
    },
    stepPillActive: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main + '10',
        border: `1.5px solid ${theme.palette.primary.main}44`,
    },
    stepPillDone: {
        color: theme.palette.success.dark ?? theme.palette.text.primary,
        backgroundColor: '#E8F5E914',
        border: `1.5px solid ${theme.palette.success.main ?? '#4caf50'}44`,
    },
    stepTitle: {
        fontWeight: 600,
        fontSize: '1.125rem',
        marginBottom: theme.spacing(0.5),
    },
    stepLead: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        marginBottom: theme.spacing(2.5),
    },
    fieldGroup: {
        marginBottom: theme.spacing(2),
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    propertySummary: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        marginBottom: theme.spacing(2.5),
    },
    propertySummaryHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.75, 2.5),
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    propertyRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1.5, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    propertyKey: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        flexShrink: 0,
        minWidth: 80,
    },
    propertyValue: {
        fontSize: '0.875rem',
        fontWeight: 500,
        flex: 1,
        textAlign: 'right',
        marginLeft: theme.spacing(2),
    },
    propertyValueEmpty: {
        color: theme.palette.text.disabled,
        fontStyle: 'italic',
    },
    editLink: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        textDecoration: 'none',
        marginLeft: theme.spacing(1.5),
        flexShrink: 0,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    notice: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        backgroundColor: '#fefce8',
        border: '1px solid #fde68a',
        borderRadius: 10,
        padding: theme.spacing(1.5, 2),
        fontSize: '0.8125rem',
        color: '#92400e',
        marginBottom: theme.spacing(2.5),
        lineHeight: 1.5,
    },
    noticeLink: {
        fontWeight: 600,
        color: '#92400e',
        textDecoration: 'underline',
        '&:hover': {
            color: '#78350f',
        },
    },
    fieldSection: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
    },
    fieldSectionTitle: {
        fontWeight: 600,
        fontSize: '0.9375rem',
        marginBottom: theme.spacing(0.25),
    },
    fieldSectionDesc: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    serviceToggle: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1.75, 2),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        marginBottom: theme.spacing(2),
    },
    serviceToggleCopy: {
        minWidth: 0,
        flex: 1,
        paddingTop: 4,
    },
    serviceToggleLabel: {
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.4,
    },
    serviceToggleHelper: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: 4,
        lineHeight: 1.5,
    },
    serviceToggleWarn: {
        color: '#92400e',
    },
    serviceToggleLink: {
        fontWeight: 600,
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    missingLabel: {
        color: theme.palette.error.main,
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.5),
    },
    stepActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(4),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        gap: theme.spacing(2),
    },
    toneGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    toneCard: {
        border: `1.5px solid ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(2, 1.75),
        cursor: 'pointer',
        textAlign: 'left',
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.15s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
        },
    },
    toneCardSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main + '08',
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
    toneName: {
        fontWeight: 600,
        fontSize: '0.9375rem',
    },
    toneDesc: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: 4,
    },
    sectionLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.25),
        marginTop: theme.spacing(1),
    },
    provisionLoading: {
        textAlign: 'center',
        padding: theme.spacing(8, 2),
    },

    intakeSubLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: 'uppercase' as const,
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    checklistItem: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: theme.spacing(0.75, 0),
        '& .MuiCheckbox-root': {
            padding: theme.spacing(0, 1, 0, 0),
            marginTop: -2,
        },
    },
    checklistLabel: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        cursor: 'pointer',
        userSelect: 'none' as const,
    },
    checklistType: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(0.5),
    },
    metaSubValue: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },

    contentWide: {
        maxWidth: 960,
    },
    callList: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
    },
    callListLoading: {
        opacity: 0.6,
        pointerEvents: 'none' as const,
    },
    callRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        padding: theme.spacing(2, 2.5),
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 120ms ease',
        '&:last-of-type': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: theme.palette.grey[50],
        },
        [theme.breakpoints.down('xs')]: {
            flexWrap: 'wrap',
        },
    },
    callRowIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 2,
    },
    callRowBody: {
        flex: 1,
        minWidth: 0,
    },
    callRowTop: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        marginBottom: 2,
    },
    callRowCaller: {
        fontWeight: 600,
        fontSize: '0.9375rem',
        lineHeight: 1.3,
    },
    callRowDuration: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    callRowMeta: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.75),
    },
    callRowSummary: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
    },
    callRowSummaryEmpty: {
        fontStyle: 'italic',
        color: theme.palette.text.disabled,
    },
    callRowRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        flexShrink: 0,
        marginTop: 4,
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            paddingLeft: 56,
            marginTop: 0,
        },
    },
    callChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: '0.6875rem',
        fontWeight: 600,
        padding: theme.spacing(0.25, 0.75),
        borderRadius: 6,
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap' as const,
    },
    callChipActive: {
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
    },
    callChipLead: {
        backgroundColor: '#ecfdf5',
        color: '#15803d',
        border: 'none',
        cursor: 'pointer',
        font: 'inherit',
        lineHeight: 'inherit',
        '&:hover': {
            backgroundColor: '#d1fae5',
        },
    },
    callChevron: {
        color: theme.palette.text.disabled,
        fontSize: 20,
    },
    pagination: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1.5, 2.5),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
    paginationInfo: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },

    callDetailCard: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2.5, 3),
        marginBottom: theme.spacing(2),
    },
    callDetailMeta: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    callDetailMetaItem: {
        backgroundColor: theme.palette.grey[50],
        borderRadius: 10,
        padding: theme.spacing(1.5, 1.75),
        '& > :first-child': {
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: 'uppercase' as const,
            color: theme.palette.text.secondary,
            marginBottom: 4,
        },
        '& > :last-child': {
            fontSize: '0.9375rem',
            fontWeight: 600,
        },
    },
    callSummaryText: {
        fontSize: '0.9375rem',
        color: '#374151',
        lineHeight: 1.55,
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    sectionHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
    sectionHeadLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    sectionHeadIcon: {
        width: 28,
        height: 28,
        borderRadius: 8,
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    endedChip: {
        fontWeight: 600,
        height: 24,
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
    },
    leadLink: {
        color: theme.palette.primary.main,
        fontWeight: 600,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    transcriptThread: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        maxHeight: 520,
        overflow: 'auto',
        padding: theme.spacing(0.5, 0.25),
    },
    transcriptTurn: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '88%',
    },
    transcriptTurnAi: {
        alignItems: 'flex-start',
    },
    transcriptTurnUser: {
        alignItems: 'flex-end',
        alignSelf: 'flex-end',
    },
    transcriptSpeaker: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase' as const,
        marginBottom: 4,
        padding: theme.spacing(0, 0.5),
    },
    transcriptSpeakerAi: {
        color: theme.palette.primary.main,
    },
    transcriptSpeakerUser: {
        color: theme.palette.text.secondary,
    },
    transcriptBubble: {
        borderRadius: 12,
        padding: theme.spacing(1.25, 1.75),
        fontSize: '0.875rem',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap' as const,
        wordBreak: 'break-word' as const,
    },
    transcriptBubbleAi: {
        backgroundColor: theme.palette.primary.main + '12',
        color: theme.palette.text.primary,
        borderBottomLeftRadius: 4,
    },
    transcriptBubbleUser: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        borderBottomRightRadius: 4,
    },
    transcriptBubbleOther: {
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
    },
    audioPlayer: {
        width: '100%',
        marginTop: theme.spacing(0.5),
        borderRadius: 8,
    },
    recordingBox: {
        backgroundColor: theme.palette.grey[50],
        borderRadius: 10,
        padding: theme.spacing(1.5, 2),
    },
    emptyArtifact: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        padding: theme.spacing(3, 2),
        backgroundColor: theme.palette.grey[50],
        borderRadius: 10,
    },
    dashboardLink: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
        padding: theme.spacing(2, 2.5),
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
            backgroundColor: theme.palette.grey[50],
            borderColor: theme.palette.primary.main + '55',
        },
    },
    dashboardLinkIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    dashboardLinkTitle: {
        fontWeight: 600,
        fontSize: '0.9375rem',
        lineHeight: 1.3,
    },
    dashboardLinkDesc: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },

    workspace: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        padding: theme.spacing(3, 4, 4),
        boxSizing: 'border-box',
    },
    headerBrand: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    headerBrandIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    liveDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#22c55e',
        boxShadow: '0 0 0 4px rgba(34,197,94,0.16)',
        marginRight: 4,
    },
    pausedDot: {
        backgroundColor: '#eab308',
        boxShadow: '0 0 0 4px rgba(234,179,8,0.16)',
    },
    headerNumber: {
        fontWeight: 600,
        fontSize: '0.9375rem',
        letterSpacing: 0.2,
    },
    metricRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5),
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    metricCard: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.75, 2),
        minWidth: 0,
    },
    metricTop: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    metricIcon: {
        width: 28,
        height: 28,
        borderRadius: 8,
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    metricLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    metricValue: {
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },
    metricHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: 4,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },
    tabBar: {
        display: 'flex',
        gap: theme.spacing(0.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2.5),
        overflowX: 'auto' as const,
    },
    tabBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        padding: theme.spacing(1.25, 1.5),
        borderBottom: '2px solid transparent',
        marginBottom: -1,
        whiteSpace: 'nowrap' as const,
        '&:hover': {
            color: theme.palette.text.primary,
        },
    },
    tabBtnActive: {
        color: theme.palette.primary.main,
        borderBottomColor: theme.palette.primary.main,
    },
    tabPane: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    tabPaneNarrow: {
        maxWidth: 720,
        width: '100%',
    },
    callSplit: {
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '340px minmax(0, 1fr)',
        gap: theme.spacing(2),
        minHeight: 540,
        alignItems: 'stretch',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    callListPane: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
    },
    callSearch: {
        padding: theme.spacing(1.5, 1.5, 1),
    },
    callListScroll: {
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
    },
    logItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.5, 1.75),
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:hover': {
            backgroundColor: theme.palette.grey[50],
        },
    },
    logItemActive: {
        backgroundColor: theme.palette.primary.main + '0D',
        boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    },
    logAvatar: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main + '18',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    logItemBody: {
        flex: 1,
        minWidth: 0,
    },
    logItemTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: theme.spacing(1),
    },
    logName: {
        fontWeight: 700,
        fontSize: '0.875rem',
        lineHeight: 1.3,
    },
    logTime: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    logIntent: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },
    logBadges: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 4,
        marginTop: 8,
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '0.6875rem',
        fontWeight: 600,
        padding: theme.spacing(0.15, 0.75),
        borderRadius: 999,
        lineHeight: 1.6,
    },
    pillGreen: {
        backgroundColor: '#ecfdf5',
        color: '#15803d',
    },
    pillPrimary: {
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
    },
    pillGray: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.secondary,
    },
    detailPane: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2.5, 3),
        minHeight: 0,
        overflow: 'auto',
    },
    detailHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    detailName: {
        fontWeight: 700,
        fontSize: '1.125rem',
        lineHeight: 1.25,
    },
    detailSub: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 4,
    },
    playBtn: {
        textTransform: 'none' as const,
        fontWeight: 600,
        flexShrink: 0,
    },
    detailPills: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 6,
        margin: theme.spacing(1.5, 0, 2),
    },
    transcriptLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: 'uppercase' as const,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.25),
    },
    bubbleRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
        maxWidth: '92%',
    },
    bubbleRowUser: {
        alignSelf: 'flex-end',
        flexDirection: 'row-reverse' as const,
    },
    speakerAvatar: {
        width: 26,
        height: 26,
        borderRadius: '50%',
        fontSize: '0.625rem',
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    speakerAvatarAi: {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
    },
    speakerAvatarUser: {
        backgroundColor: '#111827',
        color: '#fff',
    },
    recordingHero: {
        background: `linear-gradient(180deg, ${theme.palette.primary.main}14 0%, ${theme.palette.grey[50]} 100%)`,
        borderRadius: 12,
        padding: theme.spacing(2, 2),
        marginBottom: theme.spacing(2),
        border: `1px solid ${theme.palette.primary.main}22`,
    },

    filterDrawer: {
        width: 340,
        display: 'flex',
        flexDirection: 'column' as const,
        height: '100%',
        padding: theme.spacing(2.5),
    },
    filterDrawerHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        flexShrink: 0,
    },
    filterDrawerIcon: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: 'rgba(37,99,235,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    filterDrawerBody: {
        flex: 1,
        overflowY: 'auto' as const,
        paddingBottom: theme.spacing(1),
    },
    filterDrawerSection: {
        marginBottom: theme.spacing(2.5),
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#fafbfc',
        '& .MuiFormControlLabel-root': {
            marginLeft: -4,
            marginRight: 0,
            '& .MuiTypography-root': {
                fontSize: '0.8125rem',
            },
        },
        '& .MuiRadio-root': {
            padding: 6,
        },
        '& .MuiRadioGroup-root': {
            marginTop: theme.spacing(0.75),
        },
    },
    filterDrawerLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
        display: 'block',
    },
    filterDrawerFooter: {
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingTop: theme.spacing(2),
        flexShrink: 0,
    },
    activeChips: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap' as const,
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    filterChip: {
        height: 24,
        fontSize: '0.6875rem',
        fontWeight: 500,
        backgroundColor: '#eff6ff',
        color: '#2563eb',
        border: '1px solid #bfdbfe',
        '& .MuiChip-deleteIcon': {
            fontSize: 14,
            color: '#93c5fd',
            '&:hover': {
                color: '#2563eb',
            },
        },
    },
    filterChipClear: {
        height: 24,
        fontSize: '0.6875rem',
        fontWeight: 600,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },

    btnPrimary: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.875rem',
        borderRadius: 10,
        padding: theme.spacing(1, 2.5),
        boxShadow: 'none',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        },
    },
    btnPrimaryLg: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.9375rem',
        borderRadius: 10,
        padding: theme.spacing(1.25, 3),
        boxShadow: 'none',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        },
    },
    btnOutlined: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.8125rem',
        borderRadius: 10,
        padding: theme.spacing(0.75, 2),
        borderColor: theme.palette.divider,
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.main + '0A',
        },
    },
    btnGhost: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.8125rem',
        borderRadius: 10,
        padding: theme.spacing(0.75, 2),
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
        },
    },
    btnSmall: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.75rem',
        borderRadius: 8,
        padding: theme.spacing(0.5, 1.5),
    },
    btnIcon: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.8125rem',
        borderRadius: 10,
        padding: theme.spacing(0.75, 2),
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(0.75),
        },
    },

    knowledgeGrid: {
        display: 'grid',
        gap: theme.spacing(2),
        [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: '1fr 1fr',
            alignItems: 'start',
        },
    },
    docSection: {},
    docWarning: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        borderRadius: 10,
        backgroundColor: '#fefce8',
        border: '1px solid #fde68a',
        fontSize: '0.8125rem',
        color: '#92400e',
        marginBottom: theme.spacing(2),
        lineHeight: 1.5,
    },
    docBudgetBar: {
        marginBottom: theme.spacing(2),
    },
    docBudgetLabel: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
    },
    docDropZone: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(3),
        textAlign: 'center' as const,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        backgroundColor: theme.palette.grey[50],
        marginBottom: theme.spacing(2),
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.main + '06',
        },
    },
    docDropZoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main + '0C',
    },
    docDropZoneDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        '&:hover': {
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.grey[50],
        },
    },
    docList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(1),
    },
    docRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        transition: 'all 0.12s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main + '66',
            backgroundColor: theme.palette.grey[50],
        },
    },
    docRowSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main + '06',
    },
    docFileIcon: {
        width: 36,
        height: 36,
        borderRadius: 8,
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    docFileMeta: {
        flex: 1,
        minWidth: 0,
    },
    docFileName: {
        fontWeight: 600,
        fontSize: '0.8125rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },
    docFileSub: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
    },
    docPreview: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        borderRadius: 8,
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        fontSize: '0.8125rem',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap' as const,
        maxHeight: 200,
        overflowY: 'auto' as const,
        color: theme.palette.text.secondary,
    },
    docEmpty: {
        textAlign: 'center' as const,
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        padding: theme.spacing(3),
    },
    questionPage: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(2),
    },
    questionToolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap' as const,
    },
    questionSearch: {
        flex: '1 1 220px',
        minWidth: 180,
    },
    questionDate: {
        width: 150,
    },
    questionSort: {
        width: 160,
    },
    questionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: theme.spacing(2),
    },
    questionTitle: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.3,
    },
    questionSubtitle: {
        marginTop: 4,
        maxWidth: 520,
        fontSize: '0.875rem',
        lineHeight: 1.5,
        color: theme.palette.text.secondary,
    },
    questionEmptyCopy: {
        margin: '8px auto 0',
        maxWidth: 460,
        fontSize: '0.875rem',
        lineHeight: 1.5,
        color: theme.palette.text.secondary,
    },
    questionSegment: {
        display: 'inline-flex',
        alignSelf: 'flex-start',
        backgroundColor: theme.palette.grey[100],
        borderRadius: 12,
        padding: 4,
        gap: 4,
    },
    questionSegmentBtn: {
        appearance: 'none' as const,
        border: 'none',
        background: 'transparent',
        borderRadius: 9,
        padding: '7px 12px',
        font: 'inherit',
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        '&:hover': {
            color: theme.palette.text.primary,
        },
    },
    questionSegmentBtnActive: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: '0 1px 3px rgba(26, 45, 55, 0.12)',
    },
    questionCount: {
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        borderRadius: 9,
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.secondary,
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: '18px',
        textAlign: 'center' as const,
    },
    questionCountActive: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
    },
    questionCountAnswered: {
        backgroundColor: '#ecfdf5',
        color: '#15803d',
    },
    questionList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(1.5),
    },
    questionCard: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 14,
        boxShadow: '0 2px 10px rgba(26, 45, 55, 0.06)',
        overflow: 'hidden',
    },
    questionCardPending: {
        borderColor: '#fcd34d',
    },
    questionCardAnswered: {
        borderColor: '#bbf7d0',
    },
    questionCardBody: {
        padding: theme.spacing(2.25, 2.5),
    },
    questionQuote: {
        fontSize: '1.0625rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
    },
    questionMeta: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.25),
    },
    questionChip: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 8px',
        borderRadius: 999,
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    questionChipHot: {
        backgroundColor: '#fff7ed',
        color: '#c2410c',
    },
    questionChipLive: {
        backgroundColor: '#ecfdf5',
        color: '#15803d',
    },
    questionCallLink: {
        appearance: 'none' as const,
        border: 'none',
        background: 'transparent',
        padding: 0,
        font: 'inherit',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    questionComposer: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5),
        borderRadius: 12,
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
    },
    questionComposerLabel: {
        display: 'block',
        marginBottom: 8,
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase' as const,
        color: theme.palette.text.secondary,
    },
    questionField: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 10,
        },
        '& .MuiOutlinedInput-input': {
            fontSize: '0.9375rem',
            lineHeight: 1.5,
        },
    },
    questionComposerFoot: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1.25),
    },
    questionHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    questionActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
    },
    questionDismissedAnswer: {
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.5, 1.75),
        borderRadius: 12,
        backgroundColor: theme.palette.grey[50],
        color: theme.palette.text.secondary,
        fontSize: '0.9375rem',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap' as const,
    },
    questionEmpty: {
        textAlign: 'center' as const,
        padding: theme.spacing(6, 3),
    },
    questionBadge: {
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        borderRadius: 9,
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: '18px',
        textAlign: 'center' as const,
    },
    '@keyframes voiceLivePulse': {
        '0%': { boxShadow: '0 0 0 0 rgba(34, 197, 94, 0.45)' },
        '70%': { boxShadow: '0 0 0 8px rgba(34, 197, 94, 0)' },
        '100%': { boxShadow: '0 0 0 0 rgba(34, 197, 94, 0)' },
    },
    '@keyframes voiceLiveIn': {
        '0%': { opacity: 0, transform: 'translateY(-8px)' },
        '100%': { opacity: 1, transform: 'translateY(0)' },
    },
    liveCalls: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        boxSizing: 'border-box',
    },
    liveBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        width: '100%',
        textAlign: 'left' as const,
        cursor: 'pointer',
        border: '1px solid rgba(34, 197, 94, 0.4)',
        borderRadius: 12,
        backgroundColor: 'rgba(34, 197, 94, 0.08)',
        padding: theme.spacing(1.25, 1.75),
        font: 'inherit',
        color: 'inherit',
        animation: '$voiceLiveIn 0.28s ease-out',
        '&:hover': {
            backgroundColor: 'rgba(34, 197, 94, 0.14)',
        },
    },
    liveCallDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: '#22c55e',
        flexShrink: 0,
        animation: '$voiceLivePulse 1.6s ease-out infinite',
    },
    liveCallDotEnded: {
        backgroundColor: theme.palette.grey[500],
        boxShadow: 'none',
        animation: 'none',
    },
    liveBannerBody: {
        minWidth: 0,
        flex: 1,
    },
    liveBannerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        fontWeight: 600,
        fontSize: '0.9375rem',
    },
    liveBannerCaller: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },
    liveBannerPreview: {
        marginTop: 2,
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },
    liveBannerAction: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: '#15803d',
    },
    liveDialogPaper: {
        borderRadius: 16,
        overflow: 'hidden',
        width: 920,
        maxWidth: '94vw',
        maxHeight: '92vh',
    },
    liveDialogTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 2.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    liveDialogAvatar: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: 'rgba(21, 128, 61, 0.12)',
        color: '#15803d',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.875rem',
        flexShrink: 0,
    },
    liveDialogHeading: {
        minWidth: 0,
        flex: 1,
    },
    liveDialogNameRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    liveDialogName: {
        fontWeight: 600,
        fontSize: '1.05rem',
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },
    liveStatus: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
        borderRadius: 999,
        padding: '2px 8px',
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase' as const,
    },
    liveStatusOn: {
        backgroundColor: 'rgba(34, 197, 94, 0.16)',
        color: '#15803d',
    },
    liveStatusEnded: {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.secondary,
    },
    liveStatusDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        backgroundColor: '#22c55e',
        animation: '$voiceLivePulse 1.6s ease-out infinite',
    },
    liveDialogMeta: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    liveDialogTranscript: {
        height: 640,
        maxHeight: 'calc(92vh - 140px)',
        overflowY: 'auto' as const,
        padding: theme.spacing(2.5, 2.25),
        backgroundColor: '#f4f7f5',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: theme.spacing(1.75),
    },
    liveTurn: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
        maxWidth: '88%',
    },
    liveTurnUser: {
        alignSelf: 'flex-end',
        flexDirection: 'row-reverse' as const,
    },
    liveTurnAi: {
        alignSelf: 'flex-start',
    },
    liveSpeaker: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.625rem',
        fontWeight: 700,
        flexShrink: 0,
    },
    liveSpeakerAi: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.divider}`,
    },
    liveSpeakerUser: {
        backgroundColor: '#15803d',
        color: '#fff',
    },
    liveTurnBody: {
        minWidth: 0,
    },
    liveTurnLabel: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: 0.2,
        color: theme.palette.text.secondary,
        marginBottom: 4,
    },
    liveTurnLabelUser: {
        textAlign: 'right' as const,
    },
    liveBubble: {
        borderRadius: 16,
        padding: theme.spacing(1.15, 1.4),
        fontSize: '0.9375rem',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap' as const,
        boxShadow: '0 1px 1px rgba(0,0,0,0.04)',
    },
    liveBubbleAi: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderBottomLeftRadius: 4,
    },
    liveBubbleUser: {
        backgroundColor: '#15803d',
        color: '#fff',
        borderBottomRightRadius: 4,
    },
    liveDialogEmpty: {
        margin: 'auto',
        maxWidth: 280,
        textAlign: 'center' as const,
        color: theme.palette.text.secondary,
        fontSize: '0.9375rem',
        lineHeight: 1.5,
    },
    liveEndedMark: {
        alignSelf: 'center',
        marginTop: theme.spacing(1),
        padding: '4px 10px',
        borderRadius: 999,
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    liveDialogFoot: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 2.25, 1.5),
        borderTop: `1px solid ${theme.palette.divider}`,
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
}))
